import { useCurrentRelease } from "@/common";
import { useNavigateToDocument } from "@/hooks/documents";
import { useRouting } from "@/hooks/use-routing";
import { useEditAttributeModal } from "@/pages/models/release/DataModel/route/Attributes/AttributeEditModal";
import DropdownMenu from "@imminently/decisively-ui/DropdownMenu";
import Icon from "@imminently/decisively-ui/Icon";
import Button from "@imminently/decisively-ui/Button";
import type { GraphNode } from "@packages/commons";
import React from "react";
import { useGraphVisualisation } from "../../GraphVisualisation/hooks/useGraphVisualisation";
import type { GraphVizType } from "../../GraphVisualisation/providers/RuleGraphProvider";
import { isStrNotNullOrBlank } from "../../util/UtilString";

import stl from "./DebuggerGraphNode.module.scss";

interface GraphNodeContextMenuProps {
  node: GraphNode;
  isGoal?: boolean;
  isTargetNode: boolean;
}

const GraphNodeContextMenu = (props: GraphNodeContextMenuProps) => {
  const { node, isGoal, isTargetNode } = props;
  const openEditModal = useEditAttributeModal();
  const release = useCurrentRelease();
  const navigateToDocument = useNavigateToDocument();
  const isProdRelease = release?.env === "prod";
  const { goToPage } = useRouting();
  const { setGraphVizType, setSelectedNodeId, setRestrictedViewRootPath, rstRestrictedView } = useGraphVisualisation();

  const [isOpen, setIsOpen] = React.useState(false);
  const closeMenu = () => setIsOpen(false);

  const { definedIn, usedIn } = node;
  const hasDefinedIn = definedIn && isStrNotNullOrBlank(definedIn);
  const usedInSansDefinedIn = hasDefinedIn ? (usedIn || []).filter((doc) => doc !== definedIn) : usedIn || [];
  const hasUsedIn = usedInSansDefinedIn && usedInSansDefinedIn.length > 0;

  // -- menu handlers

  const nop = () => {};

  const handleClickEdit = () => openEditModal(node.id);

  const handleClickDebug = () => {
    if (node.id) {
      goToPage("/build/debugger", { goalId: node.id });
    }
  };

  const handleClickGoToDefinition = () => {
    if (hasDefinedIn) {
      navigateToDocument(definedIn, false);
    }
  };

  const handleViewInGraphViz = (graphVizType: GraphVizType) => () => {
    setSelectedNodeId(node.id);
    setGraphVizType(graphVizType);
  };

  const handleSetAsFocusNode = () => {
    setRestrictedViewRootPath(node.path || node.id);
  };

  const handleClearFocusNode = () => {
    rstRestrictedView();
  };

  // -- menu items

  const createMenuAction = (label: string, action: () => void) => {
    return (
      <DropdownMenu.Item
        key={label}
        onClick={() => {
          action();
          closeMenu();
        }}
      >
        <DropdownMenu.Item.Label>{label}</DropdownMenu.Item.Label>
      </DropdownMenu.Item>
    );
  };

  // --

  const renderUsedInMenuItems = () => {
    const usedInMenuItems = usedInSansDefinedIn.map((doc) => {
      return createMenuAction(
        doc,
        doc
          ? () => {
              navigateToDocument(doc, false);
            }
          : nop,
      );
    });

    return (
      <DropdownMenu.Sub>
        <DropdownMenu.SubTrigger>
          <DropdownMenu.Item.Label>Used in...</DropdownMenu.Item.Label>
        </DropdownMenu.SubTrigger>
        <DropdownMenu.SubContent>{usedInMenuItems}</DropdownMenu.SubContent>
      </DropdownMenu.Sub>
    );
  };

  const renderViewInMenuItems = () => {
    return (
      <DropdownMenu.Sub>
        <DropdownMenu.SubTrigger>
          <DropdownMenu.Item.Label>View in...</DropdownMenu.Item.Label>
        </DropdownMenu.SubTrigger>
        <DropdownMenu.SubContent>
          <DropdownMenu.Item onClick={handleViewInGraphViz("nebula")}>
            <DropdownMenu.Item.Label>Nebula</DropdownMenu.Item.Label>
          </DropdownMenu.Item>
        </DropdownMenu.SubContent>
      </DropdownMenu.Sub>
    );
  };

  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <DropdownMenu.Trigger asChild>
        <Button icon
          id={`graph_ignoreclick_kebab_menu_${node.id}`}
          className={stl.menu_icon}
          label="Menu"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Icon name="show-more" />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        sideOffset={4}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {isProdRelease ? null : createMenuAction("Edit", handleClickEdit)}
        {isGoal ? createMenuAction("Debug", handleClickDebug) : null}
        {renderViewInMenuItems()}
        {/* {createMenuAction("Verify", nop)} */}
        {/* {createMenuAction("View in...", nop)} */}
        {hasDefinedIn ? createMenuAction("Go to definition", handleClickGoToDefinition) : null}
        {hasUsedIn ? renderUsedInMenuItems() : null}
        {isTargetNode
          ? createMenuAction("Clear focus", handleClearFocusNode)
          : createMenuAction("Set as focus node", handleSetAsFocusNode)}
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

export default GraphNodeContextMenu;
